// import Vue from 'vue';
import { mapState } from 'vuex';
import swal from 'sweetalert2';
import get from 'lodash/get';
import Form from '@/utilities/Form';
import store from '@/store';

function resolveMessage(message, ...params) {
    if (typeof message === 'function')
        return message(...params);
    return message;
}

function resolveRoute(route, ...params) {
    route = resolveMessage(route, ...params);
    if (typeof route === 'string')
        return { name: route };
    return route;
}

/**
 * Index page
 */

export function generateIndexPageMixins({ entity }) {
    let actions = store.state.data[entity].meta.actions;

    return {
        data() {
            return {
                loading: false,
                ids: null,
                totalPages: null,
            };
        },
        computed: {
            ...mapState('data', {
                [entity](state) {
                    return this.ids && this.ids.map(id => state[entity].byId[id]).filter(entity => entity);
                },
            }),
            currentPage() {
                return parseInt(this.$route.query.page || 1);
            },
            baseEntityQueryParams() {
                return {
                    page: this.currentPage,
                    sortBy: this.$route.query.sortBy,
                    direction: this.$route.query.direction,
                    search: this.$route.query.search,
                };
            },
            entityQueryParams() {
                return this.baseEntityQueryParams;
            },
        },
        // This watcher is the only thing that starts a load. It is declared
        // immediate, and Vue initialises watchers before it runs created(), so a
        // created() hook calling loadEntities() as well made every index page in
        // the application fetch its list twice on mount.
        watch: {
            entityQueryParams: {
                handler(newParams, oldParams) {
                    // Don't reload if nothing has changed
                    if (oldParams && newParams && JSON.stringify(oldParams) === JSON.stringify(newParams)) {
                        return;
                    }

                    return this.loadEntities();
                },
                immediate: true,
            },
        },
        methods: {
            async loadEntities() {
                // A page whose scope comes from a prop that has not arrived yet
                // returns undefined from entityQueryParams to mean "not ready".
                // Loading anyway would ask for the entity's unscoped list, which
                // on inventory items means paginating about 157 million rows.
                if (!this.entityQueryParams) {
                    return;
                }

                this.loading = true;

                let { result, meta } = await this.performLoad();
                this.ids = result;
                this.totalPages = meta.meta && meta.meta.last_page;

                this.loading = false;
            },
            performLoad() {
                return this.$store.dispatch(`data/${actions.index}`, this.entityQueryParams);
            },
        },
    };
}

/**
 * Index page rows
 */

export function generateIndexRowMixins({ entity, deleteConfirmationTitle, deleteConfirmationText }) {
    let { single, actions } = store.state.data[entity].meta;

    return {
        props: [single],
        data() {
            return {
                loading: false,
            };
        },
        methods: {
            async confirmDelete() {
                try {
                    await swal({
                        type: 'warning',
                        title: resolveMessage(deleteConfirmationTitle, this[single]),
                        text: resolveMessage(deleteConfirmationText, this[single]),
                        showCancelButton: true,
                        showLoaderOnConfirm: true,
                        preConfirm: () => this.performDelete(),
                    });
                } catch (e) {
                    let message = get(e, 'response.data.message', 'Please try again later.');
                    swal('Error', message, 'error');
                    this.loading = false;
                }
            },
            async performDelete() {
                this.loading = true;
                await this.$store.dispatch(`data/${actions.delete}`, { [single]: this[single] });
                await this.postDelete();
            },
            postDelete() { },
        },
    };
}

/**
 * Show entity page
 */

export function generateShowEntityMixins({ entity, dontReloadIfLoaded }) {
    let { single, actions } = store.state.data[entity].meta;

    return {
        props: ['id'],
        data() {
            return {
                loading: false,
            };
        },
        computed: {
            ...mapState('data', {
                [single](state) {
                    return this.id && state[entity].byId[this.id];
                },
            }),
        },
        created() {
            this.loadEntity();
        },
        watch: {
            '$route': 'loadEntity',
            loading: 'stateChanged',
        },
        methods: {
            async loadEntity() {
                this.loading = true;

                await this.preLoad();

                if (this.id && (!dontReloadIfLoaded || !this[single]))
                    await this.$store.dispatch(`data/${actions.show}`, { id: this.id });

                await this.postLoad();

                this.loading = false;
            },
            // Some actions that can be overridden if necessary
            preLoad() { },
            postLoad() { },
            stateChanged() {
                this.$emit('stateChanged');
            },
        },
    };
}

/**
 * Create/edit form page
 */

export function generateFormMixins({ entity, header, fields, destinationPage, dontReloadIfLoaded }) {
    let { single, actions } = store.state.data[entity].meta;

    return {
        mixins: [generateShowEntityMixins({ entity, dontReloadIfLoaded })],
        data() {
            return {
                form: new Form(fields, {
                    stayDisabledAfterSuccessfulSave: true,
                    save: this.performSave,
                    onError: () => swal('Error', 'Please try again later.', 'error'),
                    onValidationError: this.handleValidationError,
                }),
            };
        },
        computed: {
            isNew() {
                return !this.id;
            },
            header() {
                return resolveMessage(header, this.isNew, this[single]);
            },
        },
        watch: {
            'form.saving': 'stateChanged',
        },
        methods: {
            postLoad() {
                this.resetForm();
            },
            resetForm() {
                this.form.reset(this[single]);
            },
            async save(...params) {
                try {
                    let { result, meta } = await this.form.save(...params);

                    await this.postSave(result, meta);

                    if (destinationPage) {
                        let d = destinationPage;
                        if (typeof d === 'function')
                            d = d.bind(this);
                        this.$router.push(resolveRoute(d, result, meta));
                    }
                } catch (e) { }
            },
            performSave(data) {
                data = this.prepareSaveData(data);
                let saveAction = (this.isNew) ? actions.store : actions.update;
                return this.$store.dispatch(`data/${saveAction}`, { [single]: data });
            },
            prepareSaveData(data) {
                return data;
            },
            // Post-save action that can be overridden by a component if necessary
            postSave(result, meta) { },
            handleValidationError(e) { },
        },
    };
}
